"use client";

import { Button, Input, Label, toast } from "@devhop/ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";

const LotRowSchema = z.object({
  lotNo: z.string(),
  qty: z.coerce.number().int().min(1),
  manufacturedDate: z.string(),
  expiryDate: z.string(),
});

/** ລະຫັດສະແກນຫນ່ວຍ — ສ້າງຄ່າເລີ່ມຕົ້ນຈາກ lot + serial (ຕັດບໍ່ເກີນ 100 ຕາມ DB) */
export const UNIT_SCAN_MAX = 100;

export function buildDefaultUnitScanCode(
  lotNo: string,
  serialNo: string,
): string {
  const l = lotNo.trim().replace(/\s+/g, "_");
  const s = serialNo.trim().replace(/\s+/g, "_");
  if (!l || !s) return "";
  const out = `${l}|${s}`;
  return out.length > UNIT_SCAN_MAX ? out.slice(0, UNIT_SCAN_MAX) : out;
}

const SerialSubItemSchema = z.object({
  serialNo: z.string(),
  unitScanCode: z.string().optional(),
});

const SerialLotGroupSchema = z.object({
  lotNo: z.string(),
  items: z.array(SerialSubItemSchema).min(1),
});

export const ReceiveFormSchema = z.object({
  branchId: z.string().optional(),
  /** Phase W — override default receive location ຕໍ່ບັນທັດ */
  locationId: z.string().optional(),
  qty: z.coerce.number().int().min(1).default(1),
  lots: z.array(LotRowSchema).min(1),
  serialLotGroups: z.array(SerialLotGroupSchema).default([]),
  note: z.string().max(2000).optional(),
});

export type ReceiveFormValues = z.infer<typeof ReceiveFormSchema>;

export function syncUnitScanForSubItem(
  form: UseFormReturn<ReceiveFormValues>,
  groupIndex: number,
  itemIndex: number,
) {
  const groups = form.getValues("serialLotGroups");
  const g = groups[groupIndex];
  if (!g) return;
  const lot = g.lotNo.trim();
  const row = g.items[itemIndex];
  if (!row) return;
  const serial = row.serialNo.trim();
  const next = buildDefaultUnitScanCode(lot, serial);
  form.setValue(
    `serialLotGroups.${groupIndex}.items.${itemIndex}.unitScanCode`,
    next,
    { shouldValidate: true, shouldDirty: true },
  );
}

export function syncAllUnitScansInGroup(
  form: UseFormReturn<ReceiveFormValues>,
  groupIndex: number,
) {
  const groups = form.getValues("serialLotGroups");
  const g = groups[groupIndex];
  if (!g?.items?.length) return;
  for (let ii = 0; ii < g.items.length; ii++) {
    syncUnitScanForSubItem(form, groupIndex, ii);
  }
}

export function SerialLotGroupBlock({
  groupIndex,
  form,
  canRemoveGroup,
  onRemoveGroup,
  disableAddSerial = false,
}: {
  groupIndex: number;
  form: UseFormReturn<ReceiveFormValues>;
  canRemoveGroup: boolean;
  onRemoveGroup: () => void;
  /** ຮັບຕາມ PO: ປິດເມື່ອຈຳນວນແຖວ serial ຮອດຂີດຈຳກັດແລ້ວ */
  disableAddSerial?: boolean;
}) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `serialLotGroups.${groupIndex}.items`,
  });

  const lotField = form.register(`serialLotGroups.${groupIndex}.lotNo`);

  return (
    <div className="rounded-lg border border-border bg-muted/25 p-3 shadow-sm">
      <div className="mb-3 flex flex-wrap items-end gap-2 border-border border-b pb-3">
        <div className="min-w-[12rem] flex-1">
          <Label className="font-medium text-xs">Lot no.</Label>
          <p className="text-[11px] text-muted-foreground leading-snug">
            ໃສ່ເລກ lot ກ່ອນ ຈາກນັ້ນເພີ່ມ serial ຕໍ່ຊິ້ນຂ້າງລຸ່ມ
          </p>
          <Input
            className="mt-1 h-9 font-mono text-sm"
            {...lotField}
            onChange={(e) => {
              lotField.onChange(e);
              queueMicrotask(() => syncAllUnitScansInGroup(form, groupIndex));
            }}
            placeholder="LOT-A"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 text-destructive"
          disabled={!canRemoveGroup}
          onClick={onRemoveGroup}
          aria-label="ລົບກຸ່ມ lot ນີ້"
        >
          <Trash2Icon className="size-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm">Serial ຕໍ່ຊິ້ນ (ຕໍ່ lot ນີ້)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1 text-xs"
            disabled={disableAddSerial}
            onClick={() => {
              append({ serialNo: "", unitScanCode: "" });
            }}
          >
            <PlusIcon className="size-3.5" />
            ເພີ່ມ serial
          </Button>
        </div>

        <div className="space-y-2 overflow-y-auto rounded-md border border-border/80 bg-card p-2">
          {fields.map((f, i) => {
            const serialField = form.register(
              `serialLotGroups.${groupIndex}.items.${i}.serialNo`,
            );
            return (
              <div
                key={f.id}
                className="grid gap-2 border-border border-b pb-2 last:border-0 sm:grid-cols-12 sm:items-end"
              >
                <div className="sm:col-span-4">
                  <Label className="text-xs">Serial</Label>
                  <Input
                    className="mt-1 h-9 font-mono text-sm"
                    {...serialField}
                    onChange={(e) => {
                      serialField.onChange(e);
                      queueMicrotask(() =>
                        syncUnitScanForSubItem(form, groupIndex, i),
                      );
                    }}
                    placeholder="SN-001"
                  />
                </div>
                <div className="sm:col-span-7">
                  <Label className="text-xs">
                    ລະຫັດສະແກນຫນ່ວຍ (ສ້າງຈາກ Lot + Serial)
                  </Label>
                  <Input
                    className="mt-1 h-9 font-mono text-sm"
                    {...form.register(
                      `serialLotGroups.${groupIndex}.items.${i}.unitScanCode`,
                    )}
                    placeholder="LOT-A|SN-001"
                  />
                </div>
                <div className="flex justify-end sm:col-span-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 text-destructive"
                    disabled={fields.length <= 1}
                    onClick={() => remove(i)}
                    aria-label="ລົບ serial ນີ້"
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function trackingLabel(mode: string): string {
  if (mode === "lot") return "Lot";
  if (mode === "lot_serial") return "Lot + Serial";
  return "ລວມ (none)";
}

export function printLotLabelCode128(input: {
  lotScanCode: string;
  lotNo: string;
  productName: string;
  shopSku: string;
  expiryDate: string | null;
}) {
  if (typeof window === "undefined") return;
  const w = window.open("", "_blank", "width=720,height=520");
  if (!w) {
    toast.error("ບໍ່ສາມາດເປີດໜ້າພິມໄດ້");
    return;
  }
  const exp = input.expiryDate
    ? new Date(`${input.expiryDate}T12:00:00`).toLocaleDateString("lo-LA")
    : "—";
  const esc = (s: string) =>
    s
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  const code = esc(input.lotScanCode);
  w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" />
<title>Print Lot Label</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
<style>
body{font-family:Arial,sans-serif;margin:24px}
.label{width:360px;border:1px solid #111;padding:12px}
.name{font-size:14px;font-weight:600;margin-bottom:6px}
.meta{font-size:12px;margin-bottom:4px}
.code{font-family:monospace;font-size:12px;margin-top:6px}
</style></head><body>
<div class="label">
<div class="name">${esc(input.productName)}</div>
<div class="meta">SKU: ${esc(input.shopSku)} · Lot: ${esc(input.lotNo)}</div>
<div class="meta">Expiry: ${esc(exp)}</div>
<svg id="barcode"></svg>
<div class="code">${code}</div>
</div>
<script>
JsBarcode("#barcode","${code}",{format:"CODE128",width:2,height:70,displayValue:false,margin:2});
window.onload=()=>window.print();
</script>
</body></html>`);
  w.document.close();
}
